import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import LoadingScreen from '../../../components/LoadingScreen'
import { DISCARD_CHANGES_CONFIRMATION } from '../../../constants/appMessages'
import SpecialFormHeaderActions from '../components/SpecialFormHeaderActions'
import {
  createSpecialPrice as createSpecialPriceRequest,
  fetchSpecialPriceCandidateProducts,
  fetchSpecialPriceClients,
  fetchSpecialPriceDetail,
  fetchSpecialPricePriceLists,
  fetchSpecialPriceQualifiers
} from '../helpers/specialPriceApi'
import {
  buildAttributeDefinitions,
  buildScopeFiltersPayload,
  createDefaultRange,
  extractErrorMessage,
  getDateValidationErrors,
  getRangeValidationMessage,
  getSpecialPriceInputError,
  getTodayISO
} from '../helpers/specialPriceHelpers'
import SpecialPriceDatesStep from './components/SpecialPriceDatesStep'
import SpecialPriceEditableScaleList from './components/SpecialPriceEditableScaleList'
import SpecialPriceProductStep from './components/SpecialPriceProductStep'
import SpecialPriceScopeStep from './components/SpecialPriceScopeStep'
import {
  buildCreateSpecialPriceCopyState,
  buildCreateSpecialPriceHeaderActions,
  buildCreateSpecialPricePayload,
  buildSelectedQualifierPayload,
  createInitialAttributeFilters,
  formatSpecialPriceProductOptionLabel,
  getFriendlyCreateSpecialPriceAlertConfig,
  syncSpecialPriceRanges,
  validateCreateSpecialPriceForm
} from './specialPriceCreateHelpers'
import {
  SPECIAL_PRICE_CREATE_ALERT_MATCHERS,
  SPECIAL_PRICE_CREATE_ALERT_SX,
  SPECIAL_PRICE_CREATE_CLIENT_MIN_SEARCH_LENGTH,
  SPECIAL_PRICE_CREATE_COPY_TITLE,
  SPECIAL_PRICE_CREATE_FORM_DESCRIPTION,
  SPECIAL_PRICE_CREATE_MESSAGES,
  SPECIAL_PRICE_CREATE_PRODUCT_PAGE_SIZE,
  SPECIAL_PRICE_CREATE_SECTION_ALERT_SX,
  SPECIAL_PRICE_CREATE_TITLE
} from './specialPriceCreateConstants'
import '../../../css/special_price.scss'

const CreateSpecialPrice = () => {
  const navigate = useNavigate()
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [productStepWarning, setProductStepWarning] = useState('')
  const [priceLists, setPriceLists] = useState([])
  const [attributeDefinitions, setAttributeDefinitions] = useState([])
  const [scopeType, setScopeType] = useState('')
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientSearchInput, setClientSearchInput] = useState('')
  const [clientOptions, setClientOptions] = useState([])
  const [isClientLoading, setIsClientLoading] = useState(false)
  const [clientError, setClientError] = useState('')
  const [attributeFilters, setAttributeFilters] = useState(createInitialAttributeFilters)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedProductOption, setSelectedProductOption] = useState(null)
  const [productSearchInput, setProductSearchInput] = useState('')
  const [productOptions, setProductOptions] = useState([])
  const [isProductLoading, setIsProductLoading] = useState(false)
  const [productNextPage, setProductNextPage] = useState(1)
  const [productHasMore, setProductHasMore] = useState(false)
  const [selectedPackageUid, setSelectedPackageUid] = useState('')
  const [ranges, setRanges] = useState([createDefaultRange()])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [productStepRequiresReconfiguration, setProductStepRequiresReconfiguration] =
    useState(false)
  const startDateInputRef = useRef(null)
  const endDateInputRef = useRef(null)
  const isProductLoadingRef = useRef(false)

  const copyId = new URLSearchParams(window.location.search).get('copy')
  const todayISO = useMemo(() => getTodayISO(), [])
  const alertConfig = useMemo(
    () => getFriendlyCreateSpecialPriceAlertConfig(alertMessage),
    [alertMessage]
  )

  const clearProductSelection = useCallback(({ preserveDependentData = false } = {}) => {
    setSelectedProduct(null)
    setSelectedProductOption(null)
    setProductSearchInput('')
    setProductOptions([])
    setIsProductLoading(false)
    setProductNextPage(1)
    setProductHasMore(false)
    setSelectedPackageUid('')
    setProductStepRequiresReconfiguration(preserveDependentData)

    if (!preserveDependentData) {
      setRanges([createDefaultRange()])
      setStartDate('')
      setEndDate('')
    }
  }, [])

  const applyCopyData = useCallback(
    (detail, availablePriceLists, availableAttributeDefinitions) => {
      const copyState = buildCreateSpecialPriceCopyState({
        detail,
        availablePriceLists,
        availableAttributeDefinitions
      })

      setScopeType(copyState.scopeType)
      setSelectedClient(copyState.selectedClient)
      setClientSearchInput(copyState.selectedClient?.label || '')
      setClientOptions(copyState.selectedClient ? [copyState.selectedClient] : [])
      setClientError(copyState.clientError)
      setAttributeFilters(copyState.attributeFilters)
      setSelectedProduct(copyState.selectedProduct)
      setSelectedProductOption(copyState.selectedProductOption)
      setProductSearchInput(copyState.selectedProductOption?.label || '')
      setProductOptions(copyState.selectedProductOption ? [copyState.selectedProductOption] : [])
      setSelectedPackageUid(copyState.selectedPackageUid)
      setProductStepRequiresReconfiguration(copyState.productStepRequiresReconfiguration)
      setRanges(copyState.ranges)
      setStartDate(copyState.startDate)
      setEndDate(copyState.endDate)
    },
    []
  )

  useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true)

      try {
        const [priceListsData, promoQualifiersData] = await Promise.all([
          fetchSpecialPricePriceLists(),
          fetchSpecialPriceQualifiers()
        ])

        const loadedPriceLists = priceListsData.table || []
        const loadedAttributeDefinitions = buildAttributeDefinitions(promoQualifiersData)

        setPriceLists(loadedPriceLists)
        setAttributeDefinitions(loadedAttributeDefinitions)

        if (copyId) {
          const copyData = await fetchSpecialPriceDetail(copyId)
          applyCopyData(copyData, loadedPriceLists, loadedAttributeDefinitions)
        }
      } catch (error) {
        console.error(error)
        setAlertMessage(SPECIAL_PRICE_CREATE_MESSAGES.initialLoadError)
      } finally {
        setIsInitialLoading(false)
      }
    }

    loadInitialData()
  }, [applyCopyData, copyId])

  const selectedPackage = useMemo(() => {
    if (!selectedProduct) return null

    return (
      selectedProduct.packages?.find((packageOption) => packageOption.uid === selectedPackageUid) ||
      null
    )
  }, [selectedPackageUid, selectedProduct])

  const productHasMissingSalesUnit =
    selectedProduct &&
    (!selectedPackageUid || !selectedPackage?.unit || String(selectedPackage?.unit).trim() === '')

  const attributeScopeUnavailable = scopeType === 'attribute' && attributeDefinitions.length === 0

  const scopeFiltersPayload = useMemo(
    () =>
      buildScopeFiltersPayload({
        attributeFilters,
        priceLists,
        attributeDefinitions
      }),
    [attributeDefinitions, attributeFilters, priceLists]
  )

  const selectedQualifierPayload = useMemo(
    () =>
      buildSelectedQualifierPayload({
        scopeType,
        selectedClient,
        attributeFilters
      }),
    [attributeFilters, scopeType, selectedClient]
  )

  const hasSelectedAttributeFilters = useMemo(
    () => attributeDefinitions.some((definition) => Boolean(attributeFilters[definition.key])),
    [attributeDefinitions, attributeFilters]
  )

  const isScopeStepValid = useMemo(() => {
    if (!scopeType) return false
    if (scopeType === 'client') return Boolean(selectedClient) && !clientError
    return hasSelectedAttributeFilters && !attributeScopeUnavailable
  }, [
    attributeScopeUnavailable,
    clientError,
    hasSelectedAttributeFilters,
    scopeType,
    selectedClient
  ])

  const rangeValidationMessage = useMemo(() => getRangeValidationMessage(ranges), [ranges])

  const rangeFieldErrors = useMemo(
    () =>
      ranges.map((range) => {
        const errors = {}
        const specialPriceError = getSpecialPriceInputError(range.special_price)

        if (specialPriceError) {
          errors.special_price = specialPriceError
        }

        if (range.max_qty === '' || range.max_qty === null || range.max_qty === undefined) {
          return errors
        }

        const maxQty = Number(range.max_qty)
        const minQty = Number(range.min_qty)

        if (Number.isNaN(maxQty) || maxQty < minQty) {
          errors.max_qty = 'No puede ser menor al minimo de venta de la escala.'
        }

        return errors
      }),
    [ranges]
  )

  const rangeInlineErrorMessage = useMemo(() => {
    const invalidPriceIndex = rangeFieldErrors.findIndex((rangeError) =>
      Boolean(rangeError.special_price)
    )

    if (invalidPriceIndex !== -1) {
      return rangeFieldErrors[invalidPriceIndex].special_price
    }

    const invalidRangeIndex = rangeFieldErrors.findIndex((rangeError) => Boolean(rangeError.max_qty))

    if (invalidRangeIndex === -1) return ''

    return `No puede ser menor al minimo de venta de la escala ${invalidRangeIndex + 1}.`
  }, [rangeFieldErrors])

  const dateValidationErrors = useMemo(
    () => getDateValidationErrors({ startDate, endDate }),
    [endDate, startDate]
  )

  const isFormValid =
    isScopeStepValid &&
    Boolean(selectedProduct) &&
    Boolean(selectedPackageUid) &&
    !productHasMissingSalesUnit &&
    !rangeValidationMessage &&
    Object.keys(dateValidationErrors).length === 0

  const hasConfiguredRanges = useMemo(
    () =>
      ranges.length > 1 ||
      ranges.some(
        (range) =>
          String(range.special_price || '').trim() !== '' ||
          (range.max_qty !== '' && range.max_qty !== null && range.max_qty !== undefined)
      ),
    [ranges]
  )

  const hasConfiguredDates = Boolean(startDate || endDate)
  const hasConfiguredDownstreamSteps = hasConfiguredRanges || hasConfiguredDates
  const shouldShowProductStepAlert =
    Boolean(selectedProduct) &&
    (productStepRequiresReconfiguration || (fieldErrors.product && !selectedProduct))
  const isPriceStepVisible = Boolean(selectedProduct) || productStepRequiresReconfiguration
  const isPriceStepValid =
    Boolean(selectedProduct) &&
    Boolean(selectedPackageUid) &&
    !productHasMissingSalesUnit &&
    !rangeValidationMessage
  const isDatesStepVisible =
    (Boolean(selectedProduct && selectedPackageUid) && hasConfiguredRanges) ||
    isPriceStepValid ||
    productStepRequiresReconfiguration ||
    hasConfiguredDates

  const productOptionsScopeKey = useMemo(() => {
    if (scopeType === 'client') {
      return `client:${selectedClient?.client?.db_ref || selectedClient?.value || ''}`
    }

    if (scopeType === 'attribute') {
      return `attribute:${attributeDefinitions
        .map((definition) => `${definition.key}:${attributeFilters[definition.key] || ''}`)
        .join('|')}`
    }

    return 'scope:empty'
  }, [attributeDefinitions, attributeFilters, scopeType, selectedClient])

  const loadClientOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.trim().length < SPECIAL_PRICE_CREATE_CLIENT_MIN_SEARCH_LENGTH) {
      return []
    }

    const data = await fetchSpecialPriceClients(inputValue)

    return (data.table || []).map((client) => ({
      value: client.db_ref,
      label: `${client.db_ref} - ${client.name}`,
      client
    }))
  }, [])

  useEffect(() => {
    if (scopeType !== 'client') {
      setClientOptions([])
      setClientSearchInput('')
      setIsClientLoading(false)
      return
    }

    const trimmedInput = clientSearchInput.trim()

    if (selectedClient && clientSearchInput === selectedClient.label) {
      setClientOptions((prev) => {
        if (prev.some((option) => option.value === selectedClient.value)) return prev
        return [selectedClient, ...prev]
      })
      setIsClientLoading(false)
      return
    }

    if (trimmedInput.length < 3) {
      setClientOptions(selectedClient ? [selectedClient] : [])
      setIsClientLoading(false)
      return
    }

    let isActive = true
    const timeoutId = window.setTimeout(async () => {
      setIsClientLoading(true)

      try {
        const options = await loadClientOptions(trimmedInput)
        if (!isActive) return

        setClientOptions(() => {
          if (selectedClient && options.every((option) => option.value !== selectedClient.value)) {
            return [selectedClient, ...options]
          }

          return options
        })
      } catch (error) {
        if (!isActive) return
        console.error(error)
        setClientOptions(selectedClient ? [selectedClient] : [])
      } finally {
        if (isActive) {
          setIsClientLoading(false)
        }
      }
    }, 400)

    return () => {
      isActive = false
      window.clearTimeout(timeoutId)
    }
  }, [clientSearchInput, loadClientOptions, scopeType, selectedClient])

  const resetScopeErrors = useCallback(() => {
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next.scope_type
      delete next.client
      delete next.attribute
      delete next.price_list_id
      return next
    })
  }, [])

  const handleScopeTypeChange = useCallback(
    (event) => {
      const value = event.target.value

      setScopeType(value)
      setSelectedClient(null)
      setClientSearchInput('')
      setClientOptions([])
      setIsClientLoading(false)
      setClientError('')
      setProductStepWarning('')
      setAttributeFilters(createInitialAttributeFilters())
      clearProductSelection()
      resetScopeErrors()
    },
    [clearProductSelection, resetScopeErrors]
  )

  const handleClientChange = useCallback(
    (option) => {
      setSelectedClient(option)
      setClientSearchInput(option?.label || '')
      setClientOptions((prev) => {
        if (!option) return []
        if (prev.some((currentOption) => currentOption.value === option.value)) return prev
        return [option, ...prev]
      })
      clearProductSelection()
      setAlertMessage('')
      setProductStepWarning('')
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next.client
        return next
      })

      if (!option) {
        setClientError('')
        return
      }

      if (!option.client?.price_list) {
        setClientError(SPECIAL_PRICE_CREATE_MESSAGES.clientPriceListMissing)
        return
      }

      setClientError('')
    },
    [clearProductSelection]
  )

  const handleClientInputChange = useCallback(
    (_event, value, reason) => {
      if (reason === 'reset') {
        setClientSearchInput(selectedClient?.label || value || '')
        return
      }

      setClientSearchInput(value)
    },
    [selectedClient]
  )

  const handleAttributeSelectChange = useCallback(
    (fieldName, option) => {
      setAttributeFilters((prev) => ({
        ...prev,
        [fieldName]: option?.value || ''
      }))
      clearProductSelection()
      setProductStepWarning('')
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next.attribute
        delete next.price_list_id
        return next
      })
    },
    [clearProductSelection]
  )

  const loadCandidateProductOptions = useCallback(
    async (inputValue, _loadedOptions, { page = 1 } = { page: 1 }) => {
      if (!isScopeStepValid) {
        return {
          options: [],
          hasMore: false,
          additional: { page: 1 }
        }
      }

      const payload =
        scopeType === 'client'
          ? {
              scope_type: 'client',
              client_db_ref: selectedClient?.client?.db_ref || selectedClient?.value,
              q: inputValue
            }
          : {
              scope_type: 'attribute',
              scope_filters: scopeFiltersPayload,
              q: inputValue
            }

      try {
        const response = await fetchSpecialPriceCandidateProducts({ page, payload })

        if (!response?.ok) {
          return {
            options: [],
            hasMore: false,
            additional: { page: 1 },
            errorMessage: extractErrorMessage(
              response?.data,
              'No se pudieron cargar los productos disponibles.'
            )
          }
        }

        const options = (response.data?.result || []).map((product) => ({
          value: product.package_uid || product.product_db_ref,
          label: formatSpecialPriceProductOptionLabel({
            code: product.product_db_ref,
            name: product.product_name,
            unit: product.unit
          }),
          product
        }))

        return {
          options,
          hasMore: page * SPECIAL_PRICE_CREATE_PRODUCT_PAGE_SIZE < (response.data?.count || 0),
          additional: { page: page + 1 }
        }
      } catch (error) {
        console.error(error)
        return {
          options: [],
          hasMore: false,
          additional: { page: 1 }
        }
      }
    },
    [isScopeStepValid, scopeFiltersPayload, scopeType, selectedClient]
  )

  const handleSelectProduct = useCallback(
    (option) => {
      setSelectedProductOption(option)
      setProductSearchInput(option?.label || '')
      setProductStepWarning('')
      setProductOptions((prev) => {
        if (!option) return []
        if (prev.some((currentOption) => currentOption.value === option.value)) return prev
        return [option, ...prev]
      })
      setSelectedProduct(option?.product || null)

      if (!option?.product) {
        setSelectedPackageUid('')
        return
      }

      setProductStepRequiresReconfiguration(false)
      if (!productStepRequiresReconfiguration) {
        setRanges([createDefaultRange()])
        setStartDate('')
        setEndDate('')
      }

      const firstPackage = option.product.packages?.[0]
      setSelectedPackageUid(option.product.package_uid || firstPackage?.uid || '')
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next.product
        delete next.ranges
        return next
      })
    },
    [productStepRequiresReconfiguration]
  )

  const handleProductInputChange = useCallback(
    (_event, value, reason) => {
      if (reason === 'reset') {
        setProductSearchInput(selectedProductOption?.label || value || '')
        return
      }

      setProductSearchInput(value)
    },
    [selectedProductOption]
  )

  const fetchProductOptions = useCallback(
    async ({ inputValue, page = 1, append = false }) => {
      if (!isScopeStepValid || isProductLoadingRef.current) return

      isProductLoadingRef.current = true
      setIsProductLoading(true)

      try {
        const result = await loadCandidateProductOptions(inputValue, [], { page })
        const nextOptions = result.options || []

        if (result.errorMessage) {
          setProductStepWarning(
            result.errorMessage.includes(SPECIAL_PRICE_CREATE_ALERT_MATCHERS.missingScopePriceList)
              ? SPECIAL_PRICE_CREATE_MESSAGES.productWarningEmpty
              : result.errorMessage
          )
        } else if (page === 1 && !inputValue.trim() && nextOptions.length === 0) {
          setProductStepWarning(SPECIAL_PRICE_CREATE_MESSAGES.productWarningEmpty)
        } else if (!selectedProduct) {
          setProductStepWarning('')
        }

        setProductOptions((prev) => {
          const mergedOptions = append ? [...prev] : []

          nextOptions.forEach((option) => {
            if (!mergedOptions.some((currentOption) => currentOption.value === option.value)) {
              mergedOptions.push(option)
            }
          })

          if (
            selectedProductOption &&
            !mergedOptions.some((option) => option.value === selectedProductOption.value)
          ) {
            mergedOptions.unshift(selectedProductOption)
          }

          return mergedOptions
        })
        setProductHasMore(result.hasMore)
        setProductNextPage(result.additional?.page || page + 1)
      } finally {
        isProductLoadingRef.current = false
        setIsProductLoading(false)
      }
    },
    [isScopeStepValid, loadCandidateProductOptions, selectedProduct, selectedProductOption]
  )

  useEffect(() => {
    if (!isScopeStepValid || Boolean(selectedProduct)) {
      setProductOptions(selectedProductOption ? [selectedProductOption] : [])
      setProductNextPage(1)
      setProductHasMore(false)
      isProductLoadingRef.current = false
      setIsProductLoading(false)
      return
    }

    if (selectedProductOption && productSearchInput === selectedProductOption.label) {
      setProductOptions((prev) => {
        if (prev.some((option) => option.value === selectedProductOption.value)) return prev
        return [selectedProductOption, ...prev]
      })
      return
    }

    let isActive = true
    const timeoutId = window.setTimeout(async () => {
      if (!isActive) return
      await fetchProductOptions({
        inputValue: productSearchInput.trim(),
        page: 1,
        append: false
      })
    }, 400)

    return () => {
      isActive = false
      window.clearTimeout(timeoutId)
    }
  }, [
    fetchProductOptions,
    isScopeStepValid,
    productOptionsScopeKey,
    productSearchInput,
    selectedProduct,
    selectedProductOption
  ])

  const handleRangeChange = useCallback(
    (index, field, value) => {
      const nextRanges = ranges.map((range, rangeIndex) => {
        if (rangeIndex !== index) return range

        if (field === 'special_price') {
          return {
            ...range,
            special_price: value
          }
        }

        return {
          ...range,
          max_qty: value === '' ? '' : Number(value)
        }
      })

      setRanges(syncSpecialPriceRanges(nextRanges))
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next.ranges
        return next
      })
    },
    [ranges]
  )

  const removeRange = useCallback((index) => {
    setRanges((prev) => syncSpecialPriceRanges(prev.filter((_, rangeIndex) => rangeIndex !== index)))
  }, [])

  const validateBeforeSave = useCallback(() => {
    const errors = validateCreateSpecialPriceForm({
      scopeType,
      selectedClient,
      clientError,
      attributeScopeUnavailable,
      hasSelectedAttributeFilters,
      selectedProduct,
      productHasMissingSalesUnit,
      rangeValidationMessage,
      dateValidationErrors
    })

    setFieldErrors(errors)
    return errors
  }, [
    attributeScopeUnavailable,
    clientError,
    dateValidationErrors,
    hasSelectedAttributeFilters,
    productHasMissingSalesUnit,
    rangeValidationMessage,
    scopeType,
    selectedClient,
    selectedProduct
  ])

  const buildPayload = useCallback(
    () =>
      buildCreateSpecialPricePayload({
        scopeType,
        selectedClient,
        scopeFiltersPayload,
        selectedProduct,
        selectedPackageUid,
        startDate,
        endDate,
        selectedQualifierPayload,
        ranges
      }),
    [
      endDate,
      ranges,
      scopeFiltersPayload,
      scopeType,
      selectedClient,
      selectedPackageUid,
      selectedProduct,
      selectedQualifierPayload,
      startDate
    ]
  )

  const handleCloseAlert = useCallback(() => setAlertMessage(''), [])

  const openNativeDatePicker = useCallback((inputRef) => {
    const input = inputRef?.current
    if (!input || input.disabled) return

    if (typeof input.showPicker === 'function') {
      input.showPicker()
      return
    }

    input.focus()
  }, [])

  const handleStartDateChange = useCallback(
    (event) => {
      const nextStartDate = event.target.value

      setStartDate(nextStartDate)
      if (endDate && nextStartDate && endDate < nextStartDate) {
        setEndDate('')
      }
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next.start_date
        delete next.end_date
        return next
      })
    },
    [endDate]
  )

  const handleEndDateChange = useCallback((event) => {
    setEndDate(event.target.value)
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next.end_date
      return next
    })
  }, [])

  const handleCancel = useCallback(
    (event) => {
      event.preventDefault()
      if (window.confirm(DISCARD_CHANGES_CONFIRMATION)) {
        navigate('/precios_especiales')
      }
    },
    [navigate]
  )

  const headerActions = useMemo(
    () =>
      buildCreateSpecialPriceHeaderActions({
        isSaving,
        isFormValid,
        onCancel: handleCancel
      }),
    [handleCancel, isFormValid, isSaving]
  )

  const handleSubmit = async (event) => {
    event.preventDefault()
    setAlertMessage('')

    const errors = validateBeforeSave()
    if (Object.keys(errors).length > 0) return

    setIsSaving(true)

    try {
      const payload = buildPayload()
      const response = await createSpecialPriceRequest(payload)
      const responsePayload = response?.data || {}

      if (!response?.ok) {
        setAlertMessage(
          extractErrorMessage(responsePayload, SPECIAL_PRICE_CREATE_MESSAGES.saveError)
        )
        return
      }

      navigate('/precios_especiales', {
        state: {
          successMessage: copyId
            ? SPECIAL_PRICE_CREATE_MESSAGES.copySuccess
            : SPECIAL_PRICE_CREATE_MESSAGES.saveSuccess
        }
      })
    } catch (error) {
      console.error(error)
      setAlertMessage(SPECIAL_PRICE_CREATE_MESSAGES.saveError)
    } finally {
      setIsSaving(false)
    }
  }

  if (isInitialLoading) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      {productStepWarning && (
        <Alert severity="warning" sx={SPECIAL_PRICE_CREATE_ALERT_SX}>
          {productStepWarning}
        </Alert>
      )}

      {alertConfig?.type === 'mui' && (
        <Alert severity="error" onClose={handleCloseAlert} sx={SPECIAL_PRICE_CREATE_ALERT_SX}>
          {alertConfig.message}
        </Alert>
      )}

      <div className="show-title">
        <h2>{copyId ? SPECIAL_PRICE_CREATE_COPY_TITLE : SPECIAL_PRICE_CREATE_TITLE}</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="index-table">
          <div className="show-area">
            <div className="create-promo-wrapper">
              <SpecialFormHeaderActions
                description={SPECIAL_PRICE_CREATE_FORM_DESCRIPTION}
                actions={headerActions}
              />

              {shouldShowProductStepAlert && (
                <Alert severity="warning" sx={SPECIAL_PRICE_CREATE_SECTION_ALERT_SX}>
                  {SPECIAL_PRICE_CREATE_MESSAGES.reconfigureProduct}
                </Alert>
              )}

              <SpecialPriceScopeStep
                attributeDefinitions={attributeDefinitions}
                attributeFilters={attributeFilters}
                attributeScopeUnavailable={attributeScopeUnavailable}
                clientError={clientError}
                clientOptions={clientOptions}
                clientSearchInput={clientSearchInput}
                fieldErrors={fieldErrors}
                isClientLoading={isClientLoading}
                onAttributeSelectChange={handleAttributeSelectChange}
                onClientChange={handleClientChange}
                onClientInputChange={handleClientInputChange}
                onScopeTypeChange={handleScopeTypeChange}
                scopeType={scopeType}
                selectedClient={selectedClient}
              />

              {isScopeStepValid && (
                <SpecialPriceProductStep
                  fieldErrors={fieldErrors}
                  fetchProductOptions={fetchProductOptions}
                  hasConfiguredDownstreamSteps={hasConfiguredDownstreamSteps}
                  isProductLoading={isProductLoading}
                  isScopeStepValid={isScopeStepValid}
                  productHasMissingSalesUnit={productHasMissingSalesUnit}
                  productHasMore={productHasMore}
                  productNextPage={productNextPage}
                  productOptions={productOptions}
                  productOptionsScopeKey={productOptionsScopeKey}
                  productSearchInput={productSearchInput}
                  productStepRequiresReconfiguration={productStepRequiresReconfiguration}
                  selectedPackage={selectedPackage}
                  selectedProduct={selectedProduct}
                  selectedProductOption={selectedProductOption}
                  onClearProduct={(preserveDependentData) =>
                    clearProductSelection({ preserveDependentData })
                  }
                  onProductInputChange={handleProductInputChange}
                  onSelectProduct={handleSelectProduct}
                />
              )}

              {isPriceStepVisible && (
                <SpecialPriceEditableScaleList
                  fieldErrors={fieldErrors}
                  onRangeChange={handleRangeChange}
                  onRemoveRange={removeRange}
                  rangeFieldErrors={rangeFieldErrors}
                  rangeInlineErrorMessage={rangeInlineErrorMessage}
                  ranges={ranges}
                  selectedPackage={selectedPackage}
                  selectedProduct={selectedProduct}
                />
              )}

              {isDatesStepVisible && (
                <SpecialPriceDatesStep
                  endDate={endDate}
                  endDateInputRef={endDateInputRef}
                  fieldErrors={fieldErrors}
                  onEndDateChange={handleEndDateChange}
                  onOpenNativeDatePicker={openNativeDatePicker}
                  onStartDateChange={handleStartDateChange}
                  selectedProduct={selectedProduct}
                  startDate={startDate}
                  startDateInputRef={startDateInputRef}
                  todayISO={todayISO}
                />
              )}
            </div>
          </div>
        </div>
      </form>
    </>
  )
}

export default CreateSpecialPrice
